import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconTextLong } from '@components/icons/text-long';
import { TextField } from '@components/text-field';
import { TextArea } from '@components/textarea';
import { getScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';
import { useTextInputActions } from './use-field-actions';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { getBuilderDisplayDefault } from '../default-value-input';
import { DefaultValueMapping, DefaultValueMappingDisplay } from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';
import { StaleConditionWarningIcon } from '../stale-condition-warning';

type Config = FieldConfig<'input-text-large'>;

const Display: Config['Display'] = ({ field }) => {
  const displayDefault = getBuilderDisplayDefault(field);
  return (
    <DefaultValueMappingDisplay field={field} overlayHeight={pxToRem(112)} multiLine>
      <TextArea
        key={displayDefault ?? 'empty'}
        label={asteriskLabel(field.label, field.isRequired)}
        description={field.description}
        placeholder={field.placeholder}
        labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
        labelIconAfter={<StaleConditionWarningIcon field={field} />}
        defaultValue={displayDefault}
      />
    </DefaultValueMappingDisplay>
  );
};

const FormBuilderTextLarge: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;
  const menuItems = useTextInputActions(field);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        menu={menuItems}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <TextField
              onChange={actions.createChangeHandler(field, 'placeholder')}
              defaultValue={field.placeholder}
              label={t('placeholder')}
            />
            <RequiredSwitch field={field} />
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Long text',
    label: 'Label',
    placeholder: 'Placeholder text',
  },
});

export const textLargeField: Config = {
  kind: 'input-text-large',
  title: t('title'),
  group: 'inputs',
  propertiesTabs: [conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconTextLong,
  Component: FormBuilderTextLarge,
};
